/** Pure Blackjack rules: shoe, hand values, dealer play, outcomes, payouts. */
import type { TableRules } from './rules.js';
import { DEFAULT_RULES } from './rules.js';
import type { Card, Rank, RoundOutcome, Suit } from './types.js';

const SUITS: Suit[] = ['S', 'H', 'D', 'C'];
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

export interface HandValue {
  total: number;
  /** True while an ace is still being counted as 11. */
  soft: boolean;
}

export function cardPoints(rank: Rank): number {
  if (rank === 'A') return 11;
  if (rank === 'K' || rank === 'Q' || rank === 'J' || rank === '10') return 10;
  return Number(rank);
}

/** Best total: aces start at 11 and demote only to avoid a bust. */
export function handValue(cards: readonly Card[]): HandValue {
  let total = 0;
  let liveAces = 0;
  for (const card of cards) {
    total += cardPoints(card.rank);
    if (card.rank === 'A') liveAces += 1;
  }
  let soft = liveAces > 0;
  while (total > 21 && liveAces > 0) {
    total -= 10;
    liveAces -= 1;
    soft = liveAces > 0;
  }
  return { total, soft };
}

/**
 * Natural: exactly two cards worth 21. A hand produced by a split can reach 21
 * on two cards but is never a natural, so callers must gate on `fromSplit`.
 */
export function isBlackjack(cards: readonly Card[]): boolean {
  return cards.length === 2 && handValue(cards).total === 21;
}

export function isBust(cards: readonly Card[]): boolean {
  return handValue(cards).total > 21;
}

/** Two cards of equal point value, so K-10 counts as a splittable pair. */
export function isPair(cards: readonly Card[]): boolean {
  return cards.length === 2 && cardPoints(cards[0]!.rank) === cardPoints(cards[1]!.rank);
}

export function isAcePair(cards: readonly Card[]): boolean {
  return cards.length === 2 && cards[0]!.rank === 'A' && cards[1]!.rank === 'A';
}

/** The state a single hand needs for every legality question below. */
export interface HandContext {
  cards: readonly Card[];
  /** Chips already staked on this hand. */
  bet: number;
  /** The owning player's remaining bankroll. */
  chips: number;
  /** True for hands created by a split. */
  fromSplit: boolean;
  /** Splits this seat has already taken this round. */
  splitsUsed: number;
}

/** Double on any two cards, if the bankroll covers it and the rules allow it here. */
export function canDouble(ctx: HandContext, rules: TableRules = DEFAULT_RULES): boolean {
  if (ctx.cards.length !== 2) return false;
  if (ctx.chips < ctx.bet) return false;
  if (ctx.fromSplit && !rules.doubleAfterSplit) return false;
  // A split ace that cannot be hit cannot be doubled either.
  if (ctx.fromSplit && ctx.cards[0]!.rank === 'A' && !rules.hitSplitAces) return false;
  return true;
}

export function canSplit(ctx: HandContext, rules: TableRules = DEFAULT_RULES): boolean {
  if (!isPair(ctx.cards)) return false;
  if (ctx.splitsUsed >= rules.maxSplits) return false;
  if (ctx.chips < ctx.bet) return false;
  if (ctx.fromSplit && isAcePair(ctx.cards) && !rules.resplitAces) return false;
  return true;
}

/** Late surrender: opening two cards only, and never on a split hand. */
export function canSurrender(ctx: HandContext, rules: TableRules = DEFAULT_RULES): boolean {
  if (!rules.surrenderAllowed) return false;
  return ctx.cards.length === 2 && !ctx.fromSplit;
}

/** A split ace normally takes exactly one card and is then done. */
export function splitAceIsLocked(
  cards: readonly Card[],
  fromSplit: boolean,
  rules: TableRules = DEFAULT_RULES,
): boolean {
  return fromSplit && !rules.hitSplitAces && cards[0]?.rank === 'A' && cards.length >= 2;
}

/** Dealer draws under 17, and on soft 17 too when the house plays H17. */
export function dealerShouldHit(
  cards: readonly Card[],
  rules: TableRules = DEFAULT_RULES,
): boolean {
  const { total, soft } = handValue(cards);
  if (total < 17) return true;
  return rules.dealerHitsSoft17 && total === 17 && soft;
}

/** Insurance is offered only against an ace. */
export function dealerShowsAce(upcard: Card | undefined): boolean {
  return upcard?.rank === 'A';
}

/** The dealer peeks at the hole card whenever the upcard could make a natural. */
export function dealerPeeks(upcard: Card | undefined): boolean {
  if (!upcard) return false;
  return cardPoints(upcard.rank) >= 10;
}

/** Insurance costs up to half the main bet and pays 2:1. */
export function maxInsurance(bet: number): number {
  return Math.floor(bet / 2);
}

/** Chips returned by an insurance wager, stake included; 0 when the dealer misses. */
export function settleInsurance(stake: number, dealerHasBlackjack: boolean): number {
  return dealerHasBlackjack ? stake * 3 : 0;
}

/** One hand, reduced to what settlement needs. */
export interface SettleInput {
  cards: readonly Card[];
  bet: number;
  /** A true natural. Split hands set this false even when they total 21. */
  blackjack: boolean;
  surrendered: boolean;
}

export interface Settlement {
  outcome: RoundOutcome;
  /** Total chips returned to the player, stake included; 0 on a loss. */
  payout: number;
  /** Profit or loss against the stake: payout minus bet. */
  net: number;
}

export function settleHand(
  hand: SettleInput,
  dealer: readonly Card[],
  rules: TableRules = DEFAULT_RULES,
): Settlement {
  const settled = (outcome: RoundOutcome, payout: number): Settlement => ({
    outcome,
    payout,
    net: payout - hand.bet,
  });

  // Surrender is taken before the dealer plays, so it ignores the dealer hand.
  if (hand.surrendered) return settled('surrender', Math.floor(hand.bet / 2));

  const dealerBJ = isBlackjack(dealer);
  if (hand.blackjack && dealerBJ) return settled('push', hand.bet);
  if (hand.blackjack) {
    return settled('blackjack', hand.bet + Math.floor(hand.bet * rules.blackjackPayout));
  }

  const playerTotal = handValue(hand.cards).total;
  if (playerTotal > 21) return settled('loss', 0);
  // A dealer natural beats every non-blackjack hand, even a three-card 21.
  if (dealerBJ) return settled('loss', 0);

  const dealerTotal = handValue(dealer).total;
  if (dealerTotal > 21 || playerTotal > dealerTotal) return settled('win', hand.bet * 2);
  if (playerTotal === dealerTotal) return settled('push', hand.bet);
  return settled('loss', 0);
}

/** The slice of the Web Crypto API this module needs, in Node and the browser alike. */
interface WebCrypto {
  getRandomValues<T extends Uint32Array>(array: T): T;
}

/**
 * Unbiased random integer in [0, max). Uses the platform CSPRNG, which exists
 * in Node 20+ and every target browser, and rejects the tail of the uint32
 * range so low values are not favoured.
 */
export function randomInt(max: number): number {
  if (max <= 1) return 0;
  const cryptoObj = (globalThis as { crypto?: WebCrypto }).crypto;
  if (!cryptoObj?.getRandomValues) return Math.floor(Math.random() * max);
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let value = 0;
  do {
    cryptoObj.getRandomValues(buf);
    value = buf[0]!;
  } while (value >= limit);
  return value % max;
}

export type Rng = (max: number) => number;

/** FNV-1a: a tiny string hash with decent dispersion for seed text. */
function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Deterministic Rng for tests and replays: same seed, same sequence on every
 * platform. mulberry32 — small and fast, no cryptographic pretensions.
 */
export function seededRng(seed: string): Rng {
  // The zero state is degenerate under mulberry32; any nonzero constant fixes it.
  let state = hashSeed(seed) || 0x9e37_79b9;
  const next = () => {
    state = (state + 0x6d2b_79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x1_0000_0000;
  };
  return (max) => (max <= 1 ? 0 : Math.floor(next() * max));
}

/** Fisher-Yates over a copy; the input array is never mutated. */
export function shuffle<T>(items: readonly T[], rng: Rng = randomInt): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = rng(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function createShoe(decks: number = DEFAULT_RULES.decks, rng: Rng = randomInt): Card[] {
  const cards: Card[] = [];
  for (let d = 0; d < decks; d += 1) {
    for (const suit of SUITS) {
      for (const rank of RANKS) cards.push({ rank, suit });
    }
  }
  return shuffle(cards, rng);
}

export const shoeSize = (decks: number): number => decks * 52;

export function shoeNeedsReshuffle(
  shoe: readonly Card[],
  decks: number = DEFAULT_RULES.decks,
  penetration: number = DEFAULT_RULES.penetration,
): boolean {
  return shoe.length <= shoeSize(decks) * penetration;
}
